import { useRef } from "react";

import type { Vasp } from "~/v2/api/rest/cryptoWallet";
import { isVaspDid } from "../schema";
import { MIN_QUERY_LENGTH, type VaspSearchState } from "../useVaspSearch";

export const MANUAL_ENTRY = "MANUAL_ENTRY" as const;
export type ManualEntry = typeof MANUAL_ENTRY;
export const DID_ENTRY = "DID_ENTRY" as const;
export type DidEntry = typeof DID_ENTRY;
export type Selection = Vasp | ManualEntry | DidEntry | null;

export const isVasp = (value: Selection): value is Vasp =>
  value !== null && value !== MANUAL_ENTRY && value !== DID_ENTRY;

interface UseVaspSearchDropdownControllerProps {
  vaspSearch: VaspSearchState;
}

export const useVaspSearchDropdownController = ({
  vaspSearch,
}: UseVaspSearchDropdownControllerProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const selection: Selection = vaspSearch.selectedVasp
    ? vaspSearch.selectedVasp
    : vaspSearch.isManualEntry
      ? MANUAL_ENTRY
      : null;

  const onChange = (value: Selection) => {
    if (value === null) {
      vaspSearch.onClearSelection();
      return;
    }
    if (value === MANUAL_ENTRY) {
      vaspSearch.onSelectManualEntry();
      return;
    }
    if (value === DID_ENTRY) {
      vaspSearch.onSelectDidEntry(vaspSearch.query.trim());
      return;
    }
    vaspSearch.onSelectVasp(value);
  };

  const onQueryChange = (value: string) => {
    if (vaspSearch.isManualEntry) {
      return;
    }
    vaspSearch.setQuery(value);
    if (vaspSearch.selectedVasp) {
      vaspSearch.onClearSelection();
    }
  };

  const displayValue = (value: Selection): string => {
    if (isVasp(value)) {
      // A directly-entered DID synthesizes a Vasp whose name starts empty;
      // fall back to the DID so the input doesn't blank out on selection.
      return value.name || (value.did ?? value.id);
    }
    if (value === MANUAL_ENTRY) {
      return "Manual Entry";
    }
    return vaspSearch.query;
  };

  const onClear = () => {
    vaspSearch.onClearSelection();
    inputRef.current?.focus();
  };

  const isLocked = !!vaspSearch.selectedVasp || vaspSearch.isManualEntry;
  const showPanel = !isLocked && vaspSearch.query.length >= MIN_QUERY_LENGTH;
  const showClearButton = !!(vaspSearch.selectedVasp || vaspSearch.query);

  // Offered as soon as the text looks like a DID, independent of the debounced
  // search, so pasting one doesn't sit behind loading skeletons.
  const didCandidate = isVaspDid(vaspSearch.query)
    ? vaspSearch.query.trim()
    : null;

  return {
    inputRef,
    selection,
    isLocked,
    showPanel,
    showClearButton,
    didCandidate,
    onChange,
    onQueryChange,
    displayValue,
    onClear,
  };
};

export type UseVaspSearchDropdownControllerReturn = ReturnType<
  typeof useVaspSearchDropdownController
>;
